// src/pages/QueuePage.js — v2: QR-bill scan ingestion at Stock Removal
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { get, post, getUser } from '../utils/api';
import InvoiceCard from '../components/InvoiceCard';
import { Spinner } from '../components/Shared';
import CheckingAssistant from '../components/CheckingAssistant';

const STAGE_CONFIG = {
  2: {
    title: 'Stock Removal', color: '#EAB308',
    scanLabels: ['Pick invoice', 'Complete & Move to Checking'],
    hasLabelPrint: false,
    // Stock Removal accepts the QR printed on the Marg bill (a pipe-delimited
    // payload) as well as a plain invoice number.
    acceptsQr: true,
  },
  3: {
    title: 'Checking', color: '#2563EB',
    scanLabels: ['Pick invoice', 'Print label & Complete — auto-moves to Ready for Delivery'],
    hasLabelPrint: true,
    acceptsQr: false,
  },
};

export default function QueuePage({ stage }) {
  const config = STAGE_CONFIG[stage];
  const user = getUser();

  const [queue, setQueue] = useState([]);
  const [myInvoices, setMyInvoices] = useState([]);
  const [stageMap, setStageMap] = useState({});
  const [scanCounts, setScanCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [stats, setStats] = useState({ invoices_today: 0, completed_today: 0 });
  const [search, setSearch] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [printing, setPrinting] = useState(false);
  const [checkingAssistantFor, setCheckingAssistantFor] = useState(null); // invoice_number, or null

  const scanRef = useRef(null);

  const loadQueue = useCallback(async () => {
    try {
      const data = await get(`/api/invoices/queue/${stage}`);
      const activeIds = new Set(myInvoices.map(i => i.id));
      setQueue((data.queue || []).filter(i => !activeIds.has(i.id)));
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [stage, myInvoices]);

  const loadStats = useCallback(async () => {
    try {
      const d = await get('/api/invoices/kpi/today');
      if (d) setStats(d);
    } catch {}
  }, []);

  const loadInProgress = useCallback(async () => {
    try {
      const data = await get(`/api/invoices/in-progress/${stage}`);
      const restored = data.inProgress || [];
      if (restored.length) {
        setMyInvoices(restored);
        setStageMap(Object.fromEntries(restored.map(inv => [inv.id, inv.stage_id])));
      }
    } catch (err) {
      // Non-fatal — if this fails, the page still works, it just won't
      // restore anything picked-but-unfinished from a previous session.
      console.error('Failed to restore in-progress invoices:', err.message);
    }
  }, [stage]);

  useEffect(() => {
    loadInProgress().then(() => {
      loadQueue();
      loadStats();
    });
    const interval = setInterval(() => {
      loadQueue();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
    // Deliberately NOT depending on loadQueue/loadStats here (only on
    // mount) — loadInProgress must run and settle myInvoices BEFORE the
    // first loadQueue call, so loadQueue's own activeIds filtering
    // correctly excludes anything just restored. Re-running this whole
    // effect on every loadQueue/loadStats identity change would also
    // re-trigger loadInProgress unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePick = async (invoice) => {
    try {
      const res = await post(`/api/invoices/${invoice.id}/pick/${stage}`, { picked_by: user.user_id });
      setStageMap(prev => ({ ...prev, [invoice.id]: res.stageId }));
      setMyInvoices(prev => [...prev, invoice]);
      setQueue(prev => prev.filter(i => i.id !== invoice.id));
      setScanCounts(prev => ({ ...prev, [invoice.invoice_number]: 1 }));
      setMsg({ type: 'success', text: `Picked: ${invoice.invoice_number}` });
      setTimeout(() => setMsg(null), 2000);
    } catch (err) {
      console.error('Pick error:', err);
      setMsg({ type: 'error', text: `Pick failed: ${err.message}` });
    }
  };

  const handlePrintLabel = async (invoice) => {
    setPrinting(true);
    try {
      const token = localStorage.getItem('dms_token');
      const res = await fetch(`/api/labels/designer/${invoice.id}?token=${token}`);
      if (!res.ok) throw new Error('Failed to load label');
      const html = await res.text();
      const w = window.open('', '_blank', 'width=800,height=500');
      if (!w) throw new Error('Popup blocked — allow popups for this site');
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 800);
      setScanCounts(prev => ({ ...prev, [invoice.invoice_number]: 2 }));
      setMsg({ type: 'success', text: `Label opened for ${invoice.invoice_number}` });
      setTimeout(() => setMsg(null), 2000);
    } catch (err) {
      console.error('Print error:', err);
      setMsg({ type: 'error', text: `Print failed: ${err.message}` });
    } finally {
      setPrinting(false);
    }
  };

  const handleComplete = async (invoice) => {
    try {
      const stageId = stageMap[invoice.id];
      if (!stageId) {
        setMsg({ type: 'error', text: 'Stage ID missing - please pick again' });
        return;
      }
      const res = await post(`/api/invoices/${invoice.id}/complete/${stage}`, {
        stage_id: stageId,
        completed_by: user.user_id
      });
      setMyInvoices(prev => prev.filter(i => i.id !== invoice.id));
      setStageMap(prev => { const newMap = { ...prev }; delete newMap[invoice.id]; return newMap; });
      setScanCounts(prev => { const newCounts = { ...prev }; delete newCounts[invoice.invoice_number]; return newCounts; });
      setMsg({ type: 'success', text: `Completed: ${invoice.invoice_number} → Moving to ${res.nextStageName}` });
      loadQueue();
      loadStats();
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      console.error('Complete error:', err);
      setMsg({ type: 'error', text: `Complete failed: ${err.message}` });
    }
  };

  // Send the raw QR text to the QR backend, which creates the invoice (or
  // finds it if it was already ingested — re-scans are safe), then reload
  // this stage's queue so the new invoice can be picked straight away.
  const ingestQr = async (payload) => {
    const res = await post('/api/qr/scan', { payload });
    const data = await get(`/api/invoices/queue/${stage}`);
    const fresh = data.queue || [];
    const activeIds = new Set(myInvoices.map(i => i.id));
    setQueue(fresh.filter(i => !activeIds.has(i.id)));
    return {
      number: res.invoice.invoice_number,
      status: res.invoice.status,
      queued: fresh.find(i => i.invoice_number === res.invoice.invoice_number) || null,
    };
  };

  const processScan = async (raw) => {
    let code = raw;
    let fromQr = null;

    if (config.acceptsQr && raw.includes('|')) {
      try {
        const r = await ingestQr(raw);
        code = r.number;
        fromQr = r.queued;
        const alreadyMine = myInvoices.some(i => i.invoice_number === code);
        if (!fromQr && !alreadyMine) {
          setMsg({ type: 'error', text: `${code} is already in the system (${r.status}) and is not waiting in ${config.title}` });
          setTimeout(() => setMsg(null), 4000);
          return;
        }
      } catch (err) {
        setMsg({ type: 'error', text: `QR scan failed: ${err.message}` });
        setTimeout(() => setMsg(null), 5000);
        return;
      }
    }

    const invoice = fromQr || [...queue, ...myInvoices].find(i => i.invoice_number === code);
    if (!invoice) {
      // Checking only: take over an invoice whose Stock Removal was never finished (or never
      // started). The server closes Stock Removal and picks Checking in one transaction.
      if (Number(stage) === 3) {
        try {
          const res = await post('/api/invoices/checking-pickup', { invoice_number: code });
          const taken = res.invoice;
          setStageMap(prev => ({ ...prev, [taken.id]: res.stageId }));
          setMyInvoices(prev => [...prev, taken]);
          setQueue(prev => prev.filter(i => i.id !== taken.id));
          setScanCounts(prev => ({ ...prev, [taken.invoice_number]: 1 }));
          setMsg({ type: 'success', text: `Picked: ${taken.invoice_number}` });
          setTimeout(() => setMsg(null), 2000);
        } catch (err) {
          setMsg({ type: 'error', text: err.message || `Not found: ${code}` });
          setTimeout(() => setMsg(null), 3000);
        }
        return;
      }
      setMsg({ type: 'error', text: `Not found: ${code}` });
      setTimeout(() => setMsg(null), 2000);
      return;
    }
    const alreadyPicked = myInvoices.some(i => i.id === invoice.id);
    if (!alreadyPicked) { await handlePick(invoice); return; }
    const count = scanCounts[code] || 1;
    const maxScans = config.scanLabels.length;
    const nextScanNumber = count + 1;
    if (config.hasLabelPrint && nextScanNumber === 2) {
      await handlePrintLabel(invoice);
      await handleComplete(invoice);
    } else if (nextScanNumber >= maxScans) {
      await handleComplete(invoice);
    } else {
      setScanCounts(prev => ({ ...prev, [code]: nextScanNumber }));
    }
  };

  const handleScanSubmit = (e) => {
    e.preventDefault();
    if (scanInput.trim()) {
      processScan(scanInput.trim());
      // A QR payload is not something to filter the queue by
      if (scanInput.includes('|')) setSearch('');
      setScanInput('');
      if (scanRef.current) scanRef.current.focus();
    }
  };

  useEffect(() => {
    let buffer = '';
    let timer = null;
    function onKey(e) {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' && document.activeElement === scanRef.current) return;
      if (tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'Enter') {
        if (buffer.trim()) { processScan(buffer.trim()); buffer = ''; }
        clearTimeout(timer);
      } else if (e.key.length === 1) {
        buffer += e.key;
        clearTimeout(timer);
        timer = setTimeout(() => { buffer = ''; }, 300);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, myInvoices, scanCounts]);

  const filteredQueue = queue.filter(i =>
    !search ||
    i.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    i.party_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ width: '100%', minHeight: '100vh', backgroundColor: '#f5f7fa', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'sticky', top: 'var(--sticky-offset, 0px)', zIndex: 10, backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ padding: '10px 24px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: config.color, display: 'inline-block' }} />
            {config.title}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
            <div style={{ display: 'flex', gap: '28px' }}>
              <div>
                <p style={{ fontSize: '20px', fontWeight: 700, margin: 0, color: '#1f2937' }}>{queue.length}</p>
                <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0 0 0' }}>Waiting</p>
              </div>
              <div>
                <p style={{ fontSize: '20px', fontWeight: 700, margin: 0, color: '#1f2937' }}>{myInvoices.length}</p>
                <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0 0 0' }}>In Progress</p>
              </div>
              <div>
                <p style={{ fontSize: '20px', fontWeight: 700, margin: 0, color: '#1f2937' }}>{stats.completed_today || 0}</p>
                <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0 0 0' }}>Completed</p>
              </div>
            </div>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{user.name}</span>
            <button onClick={() => { setLoading(true); loadQueue(); loadStats(); }}
              style={{ padding: '6px 14px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', color: '#374151', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>
              Refresh
            </button>
          </div>
        </div>
        <div style={{ padding: '0 24px 10px' }}>
          <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '10px 14px' }}>
            <form onSubmit={handleScanSubmit} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                ref={scanRef}
                type="text"
                value={scanInput}
                onChange={e => { setScanInput(e.target.value); setSearch(e.target.value); }}
                placeholder={config.acceptsQr ? 'Scan the QR on the Marg bill, or type an invoice number...' : 'Scan barcode or type invoice number...'}
                style={{ flex: 1, padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace', outline: 'none' }}
                autoFocus
              />
              <button type="submit" style={{ padding: '8px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                Go
              </button>
            </form>
            <div style={{ display: 'flex', gap: '20px', marginTop: '8px', flexWrap: 'wrap' }}>
              {config.scanLabels.map((label, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                  <span style={{ width: '20px', height: '20px', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold' }}>{i + 1}</span>
                  <span style={{ color: '#6b7280' }}>→ {label}</span>
                </div>
              ))}
            </div>
            {printing && (<p style={{ fontSize: '12px', color: '#2563eb', marginTop: '8px', marginBottom: 0 }}>Opening label…</p>)}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, width: '100%', padding: '12px 24px 24px', overflowY: 'auto' }}>
        {msg && (
          <div style={{ padding: '12px 16px', marginBottom: '20px', borderRadius: '8px', backgroundColor: msg.type === 'error' ? '#fef2f2' : '#f0fdf4', color: msg.type === 'error' ? '#991b1b' : '#15803d', fontSize: '14px', border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#86efac'}` }}>
            {msg.text}
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}><Spinner /></div>
        ) : (
          <>
            {myInvoices.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0', paddingBottom: '8px', borderBottom: '2px solid #059669' }}>
                  In Progress ({myInvoices.length})
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                  {myInvoices.map(inv => {
                    const count = scanCounts[inv.invoice_number] || 1;
                    const showPrintButton = config.hasLabelPrint && count < 2;
                    return (
                      <div key={inv.id} style={{ position: 'relative' }}>
                        <InvoiceCard
                          invoice={inv}
                          stage={stage}
                          isPicked={true}
                          onComplete={() => handleComplete(inv)}
                          onPrint={showPrintButton ? () => handlePrintLabel(inv) : undefined}
                          scanCount={count}
                          scanLabels={config.scanLabels}
                        />
                        {stage === 3 && (
                          <button
                            onClick={() => setCheckingAssistantFor(inv.invoice_number)}
                            style={{
                              position: 'absolute', top: '10px', right: '10px', zIndex: 5,
                              padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                              border: '1px solid #2563eb', backgroundColor: '#eff6ff', color: '#2563eb', cursor: 'pointer',
                            }}
                          >
                            🔊 Checking Assistant
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0', paddingBottom: '8px', borderBottom: '2px solid #2563eb' }}>
                Waiting Queue ({filteredQueue.length})
              </h3>
              {filteredQueue.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af', fontSize: '16px', backgroundColor: 'white', borderRadius: '10px' }}>
                  No invoices waiting
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                  {filteredQueue.map(inv => (
                    <InvoiceCard key={inv.id} invoice={inv} stage={stage} onPick={() => handlePick(inv)} isPicked={false} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {checkingAssistantFor && (
        <CheckingAssistant
          invoiceNumber={checkingAssistantFor}
          onClose={() => setCheckingAssistantFor(null)}
        />
      )}
    </div>
  );
}
